import React, { type FC, type ComponentType, useCallback, useMemo } from 'react';
import { di } from 'react-magnetic-di';
import type { RequestCreateFormState } from 'state/persisted/request-create';
import { useIssueAdjustmentsActions, useAdjustmentsEnabled } from '@atlassian/ui-modifications-core/controllers';
import { useFieldsOverrides } from './use-fields-overrides';
import { useInitializeFields } from './use-initalize-fields';
import { useInitializeNoScreenTab } from './use-initialize-tabs';
import type { RequestCreateFormWithHOCsProps } from 'view/request-create-form/types';

interface Props {
    RequestCreateForm: ComponentType<RequestCreateFormWithHOCsProps>;
    requestCreateFormProps: RequestCreateFormWithHOCsProps;
    formData: RequestCreateFormState;
}

export const EnabledFormDecorator = ({ RequestCreateForm, requestCreateFormProps, formData }: Props) => {
    di(useInitializeFields, useInitializeNoScreenTab, useFieldsOverrides, useIssueAdjustmentsActions);

    useInitializeFields({ fields: formData.fields });
    useInitializeNoScreenTab();
    const { fields } = useFieldsOverrides({ fields: formData.fields });
    const { clearAppliedValue, processChange } = useIssueAdjustmentsActions();

    const formDataWithOverrides = useMemo(() => {
        return {
            ...formData,
            fields,
        };
    }, [formData, fields]);

    const onFieldChangedByUser = useCallback(
        ({
            field,
            additionalFieldData,
        }: {
            field: string;
            value: unknown;
            notFromUserInteraction?: boolean;
            additionalFieldData?: {
                rawObject: object;
                rawValue?: unknown;
            };
        }) => {
            clearAppliedValue(field);

            processChange({
                fieldId: field,
                fieldValue: additionalFieldData?.rawValue,
                eventType: 'change',
                // To be fixed with 'process-change-gets-field-type-from-store' ff removal and core package bump
                // fieldType is missing
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any);
        },
        [clearAppliedValue, processChange]
    );

    const onFieldBlurredByUser = useCallback(
        ({
            field,
            additionalFieldData,
        }: {
            field: string;
            value: unknown;
            additionalFieldData: { rawObject: object; rawValue: unknown };
        }): void => {
            processChange({
                fieldId: field,
                fieldValue: additionalFieldData?.rawValue,
                eventType: 'blur',
                // To be fixed with 'process-change-gets-field-type-from-store' ff removal and core package bump
                // fieldType is missing
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any);
        },
        [processChange]
    );

    return (
        <RequestCreateForm
            {...requestCreateFormProps}
            formData={formDataWithOverrides}
            onFieldChangedByUser={onFieldChangedByUser}
            onFieldBlurredByUser={onFieldBlurredByUser}
        />
    );
};

const FormDecorator: FC<
    RequestCreateFormWithHOCsProps & { RequestCreateForm: ComponentType<RequestCreateFormWithHOCsProps> }
> = (props) => {
    di(useAdjustmentsEnabled, EnabledFormDecorator);

    const isUiModificationsEnabled = useAdjustmentsEnabled();

    const { RequestCreateForm, ...requestCreateFormProps } = props;
    const { formData, portalId, requestTypeId } = requestCreateFormProps;

    // https://ecosystem-platform.atlassian.net/browse/UIM-2544
    // RequestCreateForm is unstable and rendering it with wrapper cause formData to reset
    // render nothing if there are no required data
    if (!formData || !portalId || !requestTypeId) {
        return null;
    }

    // Don't wrap with UIM if it is disabled
    if (!isUiModificationsEnabled) {
        return <RequestCreateForm {...props} />;
    }

    return (
        <EnabledFormDecorator
            RequestCreateForm={RequestCreateForm}
            requestCreateFormProps={requestCreateFormProps}
            formData={formData}
        />
    );
};

/**
 * Built to wrap RequestCreateFrom with UI modifications overrides.
 * It's responsible for initialazing UIM data as well as overrides (without value).
 */
export const withUiModificationsFormDecorator = () => {
    return (RequestCreateForm: ComponentType<RequestCreateFormWithHOCsProps>) => {
        const Decorator = (props: RequestCreateFormWithHOCsProps) => (
            <FormDecorator RequestCreateForm={RequestCreateForm} {...props} />
        );
        Decorator.displayName = `withUiModificationsFormDecorator(${RequestCreateForm.displayName || RequestCreateForm.name})`;
        return Decorator;
    };
};
